import { ArrowLeftIcon } from "@phosphor-icons/react/dist/csr/ArrowLeft";
import { Formik, Form, FormikHelpers } from "formik";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import { FormInput } from "@volca/ui/components/ui/form-input";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import {
  PASSWORD_RESET_SOURCE_PARAM,
  PasswordResetError,
  PASSWORD_RESET_ERROR_CODE,
  WELCOME_EMAIL_PASSWORD_RESET_SOURCE,
  useAuth,
} from "@/core/auth";
import { AutomaticLoginFailure } from "./automatic-login-failure";
import { useWelcomeLinkValidation } from "./use-welcome-link-validation";

const resetPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
  confirm_password: Yup.string()
    .equals([Yup.ref("password")], "Passwords must match")
    .required("Password is required"),
});

interface ResetPasswordFormValues {
  password: string;
  confirm_password: string;
}

export default function ResetPassword() {
  const {
    resetPassword,
    forgotPasswordEmail: authEmail,
    forgotPasswordVerificationCode: authVerificationCode,
  } = useAuth();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  // Get email and code from either URL parameters or auth context
  const email = searchParams.get("email") || authEmail || "";
  const verificationCode =
    searchParams.get("code") || authVerificationCode || "";
  const isWelcomeEmailFlow =
    searchParams.get(PASSWORD_RESET_SOURCE_PARAM) ===
    WELCOME_EMAIL_PASSWORD_RESET_SOURCE;

  const [showPassword, setShowPassword] = useState(false);
  const [automaticLoginFailed, setAutomaticLoginFailed] = useState(false);
  const [hasStartedPasswordSubmission, setHasStartedPasswordSubmission] =
    useState(false);
  const isValidatingWelcomeLink = useWelcomeLinkValidation({
    email,
    verificationCode,
    enabled: isWelcomeEmailFlow && !hasStartedPasswordSubmission,
  });

  const handleSubmit = async (
    values: ResetPasswordFormValues,
    { setSubmitting }: FormikHelpers<ResetPasswordFormValues>,
  ) => {
    setHasStartedPasswordSubmission(true);

    try {
      if (!email || !verificationCode) {
        throw new Error("Missing email or verification code");
      }

      await resetPassword({
        password: values.password,
        email,
        verification_code: verificationCode,
      });
      toast.success(
        isWelcomeEmailFlow
          ? "Your password has been set successfully"
          : "Your password has been reset successfully",
      );
    } catch (error) {
      if (error instanceof PasswordResetError) {
        switch (error.code) {
          case PASSWORD_RESET_ERROR_CODE.invalidCode:
            toast.error(
              "This reset link is invalid or has expired. Please request a new one.",
            );
            void navigate("/signup");
            break;
          case PASSWORD_RESET_ERROR_CODE.loginFailed:
            setAutomaticLoginFailed(true);
            toast.error(error.message);
            break;
          default:
            // Surface the backend validation detail when available.
            toast.error(error.message);
        }
      } else {
        toast.error(
          error instanceof Error && error.message
            ? error.message
            : "Couldn't reset your password. Please try again.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (isValidatingWelcomeLink) {
    return (
      <Stack
        gap={4}
        justify="center"
        align="center"
        className="min-h-full w-full"
      >
        <Spinner layout="centered" />
      </Stack>
    );
  }

  return (
    <Stack
      gap={4}
      direction="row"
      align="center"
      justify="center"
      className="min-h-full w-full overflow-hidden p-6"
    >
      <Card className="w-full max-w-md gap-0 p-6 pt-8">
        {automaticLoginFailed ? (
          <AutomaticLoginFailure />
        ) : (
          <Formik
            initialValues={{ password: "", confirm_password: "" }}
            validationSchema={resetPasswordSchema}
            onSubmit={handleSubmit}
          >
            {({ getFieldProps, isSubmitting, isValid }) => (
              <Form>
                <Stack className="w-full" align="start" gap={6}>
                  <Text variant="title" render={<h1 />} className="mt-4">
                    {isWelcomeEmailFlow
                      ? "Set your password"
                      : "Reset password"}
                  </Text>

                  {isWelcomeEmailFlow && email ? (
                    <Text tone="muted">
                      Please set your password for <b>{email}</b> to finish
                      setting up your account.
                    </Text>
                  ) : null}

                  {isWelcomeEmailFlow && !email ? (
                    <Text tone="muted">
                      Please set your password to finish setting up your
                      account.
                    </Text>
                  ) : null}

                  {!isWelcomeEmailFlow && email ? (
                    <Text tone="muted">
                      Please enter your new password for <b>{email}</b> and
                      confirm it.
                    </Text>
                  ) : null}

                  {!isWelcomeEmailFlow && !email ? (
                    <Text tone="muted">
                      Please enter your new password and confirm it.
                    </Text>
                  ) : null}

                  <FormInput
                    id="password"
                    type={showPassword ? "text" : "password"}
                    label="Password"
                    placeholder="Enter your password"
                    {...getFieldProps("password")}
                    fullWidth
                    RightActionComponent={
                      <Button
                        type="button"
                        variant="link"
                        size="xs"
                        aria-pressed={showPassword}
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? "Hide" : "Show"}
                      </Button>
                    }
                  />

                  <FormInput
                    id="confirm_password"
                    type={showPassword ? "text" : "password"}
                    label="Confirm password"
                    placeholder="Confirm your password"
                    {...getFieldProps("confirm_password")}
                    fullWidth
                    RightActionComponent={
                      <Button
                        type="button"
                        variant="link"
                        size="xs"
                        aria-pressed={showPassword}
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? "Hide" : "Show"}
                      </Button>
                    }
                  />

                  <Stack className="w-full" align="start" gap={2}>
                    <Button
                      type="submit"
                      size="lg"
                      disabled={!isValid || isSubmitting}
                      className="w-full"
                      loading={isSubmitting}
                    >
                      Continue
                    </Button>

                    <Button
                      type="button"
                      size="lg"
                      variant="ghost"
                      onClick={() => navigate("/login")}
                      className="w-full"
                    >
                      <ArrowLeftIcon data-icon="inline-start" />
                      Back to sign in
                    </Button>
                  </Stack>
                </Stack>
              </Form>
            )}
          </Formik>
        )}
      </Card>
    </Stack>
  );
}
